/**
 * Hook para gerenciar o estado do wizard de onboarding
 * Inclui persistência em localStorage e navegação entre etapas
 */

import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'agendario_onboarding_session';

const initialState = {
  theme: null,
  step: 'theme', // theme, question_1, question_2, question_3, processing, completed
  responses: {
    professionalName: '',
    placeName: '',
    serviceName: '',
  },
  createdIds: {},
  result: null,
  error: null,
  completedAt: null,
};

export const useOnboardingWizard = () => {
  const [state, setState] = useState(() => {
    // SEMPRE começar com estado limpo (novo usuário = nova sessão)
    // Limpar qualquer sessão anterior
    localStorage.removeItem(STORAGE_KEY);
    console.log('[useOnboardingWizard] localStorage limpo - iniciando nova sessão');
    return initialState;
  });

  // Salvar estado no localStorage sempre que mudar
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    console.log('[useOnboardingWizard] Estado salvo no localStorage:', state);
  }, [state]);

  /**
   * Navegar para próxima pergunta
   */
  const goToQuestion = useCallback((questionNumber) => {
    setState((prev) => ({
      ...prev,
      step: `question_${questionNumber}`,
    }));
  }, []);

  /**
   * Voltar para pergunta anterior
   */
  const goBack = useCallback(() => {
    setState((prev) => {
      const currentStep = prev.step;

      // Lógica de navegação para trás
      if (currentStep === 'question_1') {
        return { ...prev, step: 'theme' };
      } else if (currentStep === 'question_2') {
        return { ...prev, step: 'question_1' };
      } else if (currentStep === 'question_3') {
        return { ...prev, step: 'question_2' };
      }

      return prev;
    });
  }, []);

  /**
   * Selecionar tema
   */
  const selectTheme = useCallback((themeId) => {
    console.log('[useOnboardingWizard] selectTheme chamado com:', themeId);
    setState((prev) => {
      const newState = {
        ...prev,
        theme: themeId,
        step: 'question_1',
      };
      console.log('[useOnboardingWizard] Novo state após selectTheme:', newState);
      return newState;
    });
  }, []);

  /**
   * Definir resposta para uma pergunta
   */
  const setResponse = useCallback((responseKey, value) => {
    setState((prev) => ({
      ...prev,
      responses: {
        ...prev.responses,
        [responseKey]: value,
      },
    }));
  }, []);

  /**
   * Iniciar processamento (etapa 7 de 7)
   */
  const startProcessing = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: 'processing',
    }));
  }, []);

  /**
   * Atualizar status de processamento
   */
  const updateProcessingStep = useCallback((currentStep, totalSteps) => {
    setState((prev) => ({
      ...prev,
      processingStep: currentStep,
      totalProcessingSteps: totalSteps,
    }));
  }, []);

  /**
   * Completar onboarding com sucesso
   */
  const completeOnboarding = useCallback((result = null) => {
    setState((prev) => ({
      ...prev,
      step: 'completed',
      createdIds: result?.createdIds || {},
      result: result?.entities || null,
      completedAt: new Date().toISOString(),
      error: null,
    }));
  }, []);

  /**
   * Definir erro durante processamento
   */
  const setError = useCallback((errorMessage) => {
    setState((prev) => ({
      ...prev,
      error: errorMessage,
      step: 'error', // Estado especial para modal de erro
    }));
  }, []);

  /**
   * Recuperar de erro (volta à última pergunta)
   */
  const recoverFromError = useCallback(() => {
    setState((prev) => {
      const lastQuestionStep = ['question_3', 'question_2', 'question_1'].find(
        (step) => prev.responses && prev.responses[Object.keys(prev.responses)[Object.keys(prev.responses).length - 1]]
      );
      return {
        ...prev,
        step: lastQuestionStep || 'question_3',
        error: null,
      };
    });
  }, []);

  /**
   * Cancelar onboarding completamente
   */
  const cancelOnboarding = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState(initialState);
  }, []);

  /**
   * Limpar sessão (após completar)
   */
  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState(initialState);
  }, []);

  // Pular onboarding - marca como completado mas sem criar nada
  const skipOnboarding = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: 'completed',
      completedAt: new Date().toISOString(),
    }));
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  /**
   * Verificar se tem uma sessão ativa
   */
  const hasActiveSession = useCallback(() => {
    return state.theme && state.step !== 'completed' && state.step !== 'theme';
  }, [state.theme, state.step]);

  return {
    // Estado
    state,
    theme: state.theme,
    step: state.step,
    responses: state.responses,
    createdIds: state.createdIds,
    result: state.result,
    error: state.error,
    completedAt: state.completedAt,
    processingStep: state.processingStep,
    totalProcessingSteps: state.totalProcessingSteps,

    // Funções
    goToQuestion,
    goBack,
    selectTheme,
    setResponse,
    startProcessing,
    updateProcessingStep,
    completeOnboarding,
    setError,
    recoverFromError,
    cancelOnboarding,
    clearSession,
    skipOnboarding,
    hasActiveSession,
  };
};
